import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import BrokerVisualPickerSheet, { type PickerBroker, type PickerNetwork } from "./BrokerVisualPickerSheet";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** The network we are adding brokers TO. */
  targetNetworkId: string;
  /** All shipper networks (so we can show source-network filter chips). */
  allNetworks: { id: string; name: string }[];
  /** Shipper's own user id (used to scope the pool query). */
  shipperUserId: string;
  /** Shipper's company id, if any (extends the pool query). */
  shipperCompanyId?: string | null;
  onAdded: () => void;
}

export default function AddBrokersToNetworkSheet({
  open, onOpenChange, targetNetworkId, allNetworks, shipperUserId, shipperCompanyId, onAdded,
}: Props) {
  const [brokers, setBrokers] = useState<PickerBroker[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      // 1. Networks owned by this shipper (by user OR by company)
      const [{ data: byUser }, { data: byCompany }] = await Promise.all([
        supabase.from("shipper_private_networks" as any).select("id").eq("shipper_user_id", shipperUserId),
        shipperCompanyId
          ? supabase.from("shipper_private_networks" as any).select("id").eq("shipper_company_id", shipperCompanyId)
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const netIds = Array.from(new Set([
        ...((byUser as any[]) || []).map((r) => r.id),
        ...((byCompany as any[]) || []).map((r) => r.id),
      ]));
      if (!netIds.length) {
        if (!cancelled) { setBrokers([]); setLoading(false); }
        return;
      }
      // 2. All accepted memberships across those networks
      const { data: memberships } = await supabase
        .from("shipper_network_brokers" as any)
        .select("network_id, broker_user_id, broker_company_id")
        .in("network_id", netIds)
        .eq("status", "accepted")
        .is("removed_at", null);

      const rows = ((memberships as any[]) || []).filter((r) => r.broker_user_id);
      const byUserMap = new Map<string, { user_id: string; company_id: string | null; network_ids: string[] }>();
      const inTarget = new Set<string>();
      for (const r of rows) {
        if (r.network_id === targetNetworkId) inTarget.add(r.broker_user_id);
        const ex = byUserMap.get(r.broker_user_id);
        if (ex) {
          if (!ex.network_ids.includes(r.network_id)) ex.network_ids.push(r.network_id);
        } else {
          byUserMap.set(r.broker_user_id, {
            user_id: r.broker_user_id,
            company_id: r.broker_company_id ?? null,
            network_ids: [r.network_id],
          });
        }
      }
      // 3. Exclude brokers already in target network
      for (const id of inTarget) byUserMap.delete(id);

      const userIds = Array.from(byUserMap.keys());
      const companyIds = Array.from(new Set(
        Array.from(byUserMap.values()).map((b) => b.company_id).filter((x): x is string => !!x)
      ));

      const [{ data: profs }, { data: cos }] = await Promise.all([
        userIds.length
          ? (supabase as any).from("profiles").select("id, first_name, last_name, email").in("id", userIds)
          : Promise.resolve({ data: [] as any[] }),
        companyIds.length
          ? supabase.from("companies").select("id, name").in("id", companyIds)
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const profMap = new Map((profs as any[] || []).map((p) => [p.id, p]));
      const coMap = new Map<string, string>(
        ((cos as any[]) || []).map((c) => [c.id as string, (c.name as string) || ""])
      );

      const list: PickerBroker[] = Array.from(byUserMap.values()).map((b) => {
        const p = profMap.get(b.user_id) as any;
        const personName = p ? [p.first_name, p.last_name].filter(Boolean).join(" ").trim() : "";
        const coName = b.company_id ? coMap.get(b.company_id) : undefined;
        return {
          user_id: b.user_id,
          display_name: String(coName || personName || p?.email || "Broker"),
          secondary_name: coName && personName ? personName : (coName && p?.email) || undefined,
          network_ids: b.network_ids,
        };
      });
      list.sort((a, b) => String(a.display_name).localeCompare(String(b.display_name)));
      if (!cancelled) {
        setBrokers(list);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, targetNetworkId, shipperUserId, shipperCompanyId]);

  const pickerNetworks: PickerNetwork[] =
    allNetworks.length > 1 ? allNetworks.filter((n) => n.id !== targetNetworkId) : [];

  const handleApply = async (ids: string[] | null) => {
    const list = ids ?? brokers.map((b) => b.user_id);
    if (!list.length) {
      onOpenChange(false);
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.rpc("add_brokers_to_network" as any, {
        p_network_id: targetNetworkId,
        p_broker_user_ids: list,
      });
      if (error) throw error;
      const added = (data as unknown as number) ?? 0;
      toast.success(added === 0 ? "No new brokers added" : `Added ${added} broker${added === 1 ? "" : "s"}`);
      onAdded();
    } catch (e: any) {
      toast.error(e?.message || "Failed to add brokers");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BrokerVisualPickerSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Add Brokers to Network"
      brokers={brokers}
      networks={pickerNetworks}
      initialSelectedIds={[]}
      allowAllAsNull={false}
      loading={loading || submitting}
      emptyMessage="No other brokers in your pool — invite some first."
      applyLabel={submitting ? "Adding…" : "Add"}
      onApply={handleApply}
    />
  );
}
